import { FileRecord } from "@/shared/domain/files";
import { FileSystemNode, isFile } from "@/shared/domain/files/filesystem";

/**
 * Extension list for robot descriptions that can be ingested as a 3D asset.
 *
 * `.xacro` is deliberately absent. It is a macro language *over* URDF, and the
 * converter does no macro expansion — offering to ingest one would produce a
 * failure the user cannot act on.
 */
export const urdfFileTypes = ["urdf"];

/** Files that can be converted into a viewable, articulated 3D asset. */
export const isUrdf = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return urdfFileTypes.some((ext) => lower.endsWith(`.${ext}`));
};

/**
 * The robot description to open as a 3D asset, or `null` when the node should
 * be opened the ordinary way (the file view for a file, navigation for a
 * directory).
 *
 * Opening a URDF means seeing the robot, not its XML, so every "open this
 * node" entry point — row double click, grid tile click, the row menu's View,
 * and its Ingest as Asset item — asks this one question instead of repeating
 * `isFile(node) && isUrdf(node.name)` and risking the copies drifting apart.
 *
 * Returns the record rather than a boolean so the `isFile` narrowing survives
 * into the caller, which needs the {@link FileRecord} to open the wizard.
 */
export function robotDescriptionToIngest(
  node: FileSystemNode,
): FileRecord | null {
  return isFile(node) && isUrdf(node.name) ? node.file : null;
}
